import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { StaggerInView, StaggerInViewItem } from "../tools/Animations";
import { HealthIntegrationPreviewCard } from "./aimmaNurse/HealthIntegrationPreviewCard";
import { healthLocalePath } from "./aimmaDoctor/healthLocalePath";
import { HealthSection } from "./HealthSection";
import { HealthSectionHeader } from "./HealthSectionHeader";
import {
    AimmaProductSuiteGroupId,
    getLocalized,
    HealthLang,
    LocalizedString,
} from "./healthTypes";
import { healthSectionBlock } from "./healthLayout";
import {
    healthCardIdFromPath,
    healthIntegrationEcosystemPreviewCardGrid,
    sortHealthEcosystemItems,
} from "./healthVisualStyles";

interface HealthSecondarySolutionsProps {
    data: DataKeys;
}

const DEFAULT_CTA_LABELS: Record<string, LocalizedString> = {
    nurse: {
        En: "Explore Aimma for Nurse",
        Zh: "探索 Aimma 護理版",
        Jp: "Aimma 看護向けを見る",
    },
    doctor: {
        En: "Explore Aimma for Doctor",
        Zh: "探索 Aimma 醫師版",
        Jp: "Aimma 医師向けを見る",
    },
    patient: {
        En: "Learn about Aimma for Patient",
        Zh: "了解 Aimma 病人版",
        Jp: "Aimma 患者向けについて",
    },
};

function resolveGroupId(cardId: string): AimmaProductSuiteGroupId {
    if (cardId === "nurse" || cardId === "doctor" || cardId === "patient") {
        return cardId;
    }
    return "doctor";
}

export function HealthSecondarySolutions({ data }: HealthSecondarySolutionsProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const items: DataKeys[] = sortHealthEcosystemItems(
        (data?.items ?? []) as DataKeys[]
    );
    const title = getLocalized(data?.title, hLang);
    const body = getLocalized(data?.body, hLang);

    if (!items.length) return null;

    return (
        <HealthSection id="cta-secondary" tight>
            <div className={healthSectionBlock}>
                <HealthSectionHeader
                    title={title}
                    titleId="cta-secondary-title"
                    subtitle={body}
                    className="mb-0 lg:mb-0"
                />
                <StaggerInView className={healthIntegrationEcosystemPreviewCardGrid}>
                    {items.map((item) => {
                        const link = (item.link as string) ?? "";
                        const cardId =
                            (item.id as string | undefined) ??
                            healthCardIdFromPath(link);
                        const comingSoon = Boolean(item.comingSoon);
                        const groupId = resolveGroupId(cardId);
                        const itemTitle = getLocalized(item.title, hLang);
                        const itemBody = getLocalized(item.text, hLang);
                        const ctaLabel = getLocalized(
                            (item.ctaLabel as LocalizedString | undefined) ??
                                DEFAULT_CTA_LABELS[cardId],
                            hLang
                        );
                        const href = comingSoon
                            ? undefined
                            : healthLocalePath(link, hLang);

                        return (
                            <StaggerInViewItem
                                key={cardId || getLocalized(item.title, hLang)}
                                direction="bottom"
                                className="h-full"
                            >
                                <HealthIntegrationPreviewCard
                                    cardId={cardId}
                                    groupId={groupId}
                                    title={itemTitle}
                                    body={itemBody}
                                    ctaLabel={ctaLabel}
                                    comingSoon={comingSoon}
                                    hLang={hLang}
                                    href={href}
                                />
                            </StaggerInViewItem>
                        );
                    })}
                </StaggerInView>
            </div>
        </HealthSection>
    );
}
